import type { PopperProps, SxProps, Theme } from "@mui/material";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

type GenericFilterMenuProps = {
  id: string;
  triggerId: string;
  triggerAriaLabel: string;
  closeAriaLabel: string;
  triggerIcon: React.ReactNode;
  children: React.ReactNode;
  onResetFilters: () => void;
  triggerTooltip?: React.ReactNode;
  triggerSx?: SxProps<Theme>;
  triggerOpenSx?: SxProps<Theme>;
  hasActiveFilters?: boolean;
  contentTestId: string;
  headerTestId: string;
  bodyTestId: string;
  popperTestId: string;
  resetButtonTestId?: string;
  placement?: PopperProps["placement"];
  fallbackPlacements?: NonNullable<PopperProps["placement"]>[];
  unmountDelayMs?: number;
  bodySx?: SxProps<Theme>;
};

const toSxArray = (sx?: SxProps<Theme>) =>
  Array.isArray(sx) ? sx : sx ? [sx] : [];

export function GenericFilterMenu({
  id,
  triggerId,
  triggerAriaLabel,
  closeAriaLabel,
  triggerIcon,
  children,
  onResetFilters,
  triggerTooltip,
  triggerSx,
  triggerOpenSx,
  hasActiveFilters = false,
  contentTestId,
  headerTestId,
  bodyTestId,
  popperTestId,
  resetButtonTestId,
  placement = "right-start",
  fallbackPlacements = ["top-end", "bottom-end", "left-start", "left-end"],
  unmountDelayMs = 100,
  bodySx,
}: GenericFilterMenuProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const unmountTimerRef = useRef<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isTriggerTooltipOpen, setIsTriggerTooltipOpen] = useState(false);
  const isOpen = Boolean(anchorEl);

  const closeTriggerTooltip = useCallback(() => {
    setIsTriggerTooltipOpen(false);
  }, []);

  const openTriggerTooltip = useCallback(() => {
    if (!isOpen) {
      setIsTriggerTooltipOpen(true);
    }
  }, [isOpen]);

  const clearUnmountTimer = useCallback(() => {
    if (unmountTimerRef.current === null) {
      return;
    }

    window.clearTimeout(unmountTimerRef.current);
    unmountTimerRef.current = null;
  }, []);

  const scheduleUnmount = useCallback(() => {
    clearUnmountTimer();

    unmountTimerRef.current = window.setTimeout(() => {
      unmountTimerRef.current = null;
      setIsMounted(false);
    }, unmountDelayMs);
  }, [clearUnmountTimer, unmountDelayMs]);

  const closeMenu = useCallback(
    (shouldRestoreFocus = false) => {
      closeTriggerTooltip();
      setAnchorEl(null);
      setIsVisible(false);
      scheduleUnmount();

      if (shouldRestoreFocus) {
        triggerRef.current?.focus();
      }
    },
    [closeTriggerTooltip, scheduleUnmount]
  );

  const toggleMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    closeTriggerTooltip();

    if (isOpen) {
      closeMenu();
      return;
    }

    triggerRef.current = event.currentTarget;
    clearUnmountTimer();
    setIsVisible(false);
    setIsMounted(true);
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    if (!anchorEl) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      setIsVisible(true);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [anchorEl]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      closeMenu(true);
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeMenu, isOpen]);

  useEffect(
    () => () => {
      clearUnmountTimer();
    },
    [clearUnmountTimer]
  );

  const triggerButton = (
    <IconButton
      ref={triggerRef}
      aria-controls={isOpen ? id : undefined}
      aria-expanded={isOpen ? "true" : undefined}
      aria-haspopup="menu"
      aria-label={triggerAriaLabel}
      data-active-filters={hasActiveFilters ? "true" : undefined}
      id={triggerId}
      onClick={toggleMenu}
      onMouseDown={closeTriggerTooltip}
      size="small"
      sx={[
        {
          color: "#6b7280",
        },
        ...(hasActiveFilters && !triggerSx
          ? toSxArray({
              backgroundColor: "rgba(7, 78, 232, 0.08)",
              color: "primary.main",
            })
          : []),
        ...toSxArray(triggerSx),
        ...(isOpen ? toSxArray(triggerOpenSx) : []),
      ]}
    >
      <Box
        component="span"
        sx={{
          alignItems: "center",
          color: "inherit",
          display: "inline-flex",
          justifyContent: "center",
          lineHeight: 0,
          "& .MuiSvgIcon-root, & svg": {
            color: "inherit",
          },
        }}
      >
        {triggerIcon}
      </Box>
    </IconButton>
  );

  return (
    <>
      {triggerTooltip ? (
        <Tooltip
          title={triggerTooltip}
          open={isTriggerTooltipOpen && !isOpen}
          onClose={closeTriggerTooltip}
          onOpen={openTriggerTooltip}
          TransitionProps={{ timeout: 0 }}
        >
          {triggerButton}
        </Tooltip>
      ) : (
        triggerButton
      )}

      {isMounted && (
        <>
          {isOpen && (
            <Box
              aria-label={closeAriaLabel}
              onClick={() => closeMenu()}
              sx={{
                backgroundColor: "transparent",
                inset: 0,
                position: "fixed",
                zIndex: (theme) => theme.zIndex.modal - 1,
              }}
            />
          )}
          <Popper
            data-testid={popperTestId}
            anchorEl={anchorEl}
            open={isOpen}
            keepMounted
            placement={placement}
            modifiers={[
              {
                name: "offset",
                options: { offset: [0, 8] },
              },
              {
                name: "preventOverflow",
                options: {
                  boundary: "viewport",
                  padding: 8,
                },
              },
              {
                name: "flip",
                options: {
                  fallbackPlacements,
                },
              },
            ]}
            sx={{ zIndex: (theme) => theme.zIndex.modal }}
          >
            <Paper
              id={id}
              role="menu"
              aria-labelledby={triggerId}
              sx={{
                border: "1px solid rgba(148, 163, 184, 0.2)",
                borderRadius: "6px",
                boxShadow: "0 18px 50px rgba(15, 23, 42, 0.18)",
                maxHeight: "70vh",
                overflow: "hidden",
                visibility: isVisible ? "visible" : "hidden",
              }}
            >
              <Box
                data-testid={contentTestId}
                sx={{
                  minHeight: 0,
                  minWidth: "330px",
                  maxWidth: "330px",
                  width: "min(330px, calc(100vw - 2rem))",
                  display: "flex",
                  flexDirection: "column",
                  maxHeight: "70vh",
                }}
              >
                <Box
                  data-testid={headerTestId}
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    gap: "7px",
                    justifyContent: "space-between",
                    margin: "10px 14px",
                  }}
                >
                  <Typography fontWeight="bold">Filter by</Typography>
                  <Button
                    data-testid={resetButtonTestId}
                    size="small"
                    variant="text"
                    onClick={onResetFilters}
                  >
                    Reset Filters
                  </Button>
                </Box>

                <Divider />

                <Box
                  data-testid={bodyTestId}
                  sx={{ ...bodySx, overflowY: "auto", flex: 1 }}
                >
                  {children}
                </Box>
              </Box>
            </Paper>
          </Popper>
        </>
      )}
    </>
  );
}
